import { useEffect, useMemo, useState } from "react";
import {
  Archive,
  Building2,
  ChevronDown,
  Coins,
  CreditCard,
  FlaskConical,
  Inbox,
  LifeBuoy,
  LogOut,
  Settings,
  Shield,
  Trash2,
  UserRound,
} from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useLocation } from "react-router-dom";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { ProjectDashboardNav } from "@/components/projects/project-dashboard-nav";
import { ProjectSettingsDialog } from "@/components/projects/project-settings-dialog";
import { Button } from "@/components/ui/button";
import type { ProjectSectionId, ProjectSectionNav } from "@/features/projects";
import { PQRFormDialog } from "@/features/pqr";
import { supabase } from "@/lib/supabase";
import { formatDisplayName } from "@/lib/utils";
import { useAuthStore } from "@/stores/authStore";
import { useProjectBookAssemblyStore } from "@/stores/project-book-assembly-store";
import { useProjectShellStore } from "@/stores/projectShellStore";
import { useSubscriptionStore } from "@/stores/subscriptionStore";

function useIsProjectRoute() {
  const { pathname } = useLocation();
  return pathname.startsWith("/projects/");
}

function getActiveSection(pathname: string): ProjectSectionId {
  if (pathname.includes("/lore")) {
    return "lore";
  }
  if (pathname.includes("/stories")) {
    return "stories";
  }
  if (pathname.includes("/entities")) {
    return "entities";
  }
  if (pathname.includes("/affinity-chart")) {
    return "affinity-chart";
  }
  if (pathname.includes("/timeline")) {
    return "timeline";
  }

  return "dashboard";
}

function getBookAssemblyLabel(status: string | null | undefined) {
  return status ? "Resume export" : "Export";
}

function isProjectAuthoringRoute(pathname: string) {
  if (/^\/projects\/[^/]+\/stories\/book-assembly(?:\/|$)/.test(pathname)) {
    return false;
  }

  return /^\/projects\/[^/]+\/(?:stories|lore|entities)(?:\/|$)/.test(pathname);
}

export function AppHeader() {
  const { t } = useTranslation();
  const [isPqrDialogOpen, setIsPqrDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const profile = useAuthStore((state) => state.profile);
  const logOut = useAuthStore((state) => state.logOut);
  const archiveProject = useProjectShellStore((state) => state.archiveProject);
  const currentProjectSummary = useProjectShellStore((state) => state.currentProjectSummary);
  const openDeleteDialog = useProjectShellStore((state) => state.openDeleteDialog);
  const creditBalance = useSubscriptionStore((state) => state.creditBalance);
  const hydrateBilling = useSubscriptionStore((state) => state.hydrateBilling);
  const tier = useSubscriptionStore((state) => state.tier);
  const isProjectRoute = useIsProjectRoute();
  const { pathname, search } = location;
  const projectId = useMemo(
    () => pathname.match(/^\/projects\/([^/]+)/)?.[1] ?? null,
    [pathname],
  );
  const bookAssemblyLaunchState = useProjectBookAssemblyStore((state) =>
    projectId ? state.launchStates[projectId] : undefined
  );

  const aiOptions = useMemo(() => {
    if (!isProjectAuthoringRoute(pathname)) {
      return [];
    }

    if (pathname.includes("/stories")) {
      return [
        {
          id: "illustration",
          label: t("story.attachments.generateIllustration"),
        },
        {
          id: "continuation",
          label: t("story.ai.continuation.title"),
        },
        {
          id: "continuity",
          label: t("story.ai.continuity.title"),
        },
        {
          id: "structural",
          label: t("story.ai.structural.title"),
        },
      ];
    }

    if (pathname.includes("/lore")) {
      return [
        {
          id: "illustration",
          label: t("story.attachments.generateIllustration"),
        },
      ];
    }

    if (pathname.includes("/entities")) {
      return [
        {
          id: "illustration",
          label: t("story.attachments.generateIllustration"),
        },
      ];
    }

    return [];
  }, [pathname, t]);

  const showAiMenu = isProjectRoute && aiOptions.length > 0;
  const activeAiFeatureId = useMemo(
    () => new URLSearchParams(search).get("ai"),
    [search],
  );
  const activeAiOptionLabel = useMemo(
    () =>
      aiOptions.find((option) => option.id === activeAiFeatureId)?.label ?? "AI",
    [activeAiFeatureId, aiOptions],
  );

  function buildAiHref(featureId: string) {
    const params = new URLSearchParams(search);
    params.set("ai", featureId);
    return `${pathname}?${params.toString()}`;
  }
  const sections = useMemo<ProjectSectionNav[]>(
    () => projectId
      ? [
          {
            id: "dashboard",
            label: t("projects.dashboard.sections.dashboard"),
            href: `/projects/${projectId}`,
            availability: "available",
          },
          {
            id: "lore",
            label: t("projects.dashboard.sections.lore"),
            href: `/projects/${projectId}/lore`,
            availability: "available",
          },
          {
            id: "stories",
            label: t("projects.dashboard.sections.stories"),
            href: `/projects/${projectId}/stories`,
            availability: "available",
          },
          {
            id: "entities",
            label: t("projects.dashboard.sections.entities"),
            href: `/projects/${projectId}/entities`,
            availability: "available",
          },
          {
            id: "affinity-chart",
            label: t("projects.dashboard.sections.affinityChart"),
            href: `/projects/${projectId}/affinity-chart`,
            availability: "available",
          },
          {
            id: "timeline",
            label: t("projects.dashboard.sections.timeline"),
            href: `/projects/${projectId}/timeline`,
            availability: "available",
          },
        ]
      : [],
    [projectId, t],
  );

  useEffect(() => {
    if (!user) {
      return;
    }

    void hydrateBilling();
  }, [hydrateBilling, user]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    let refreshTimer: number | null = null;
    let refreshing = false;
    let queued = false;

    const scheduleRefresh = (delayMs = 250) => {
      if (refreshTimer !== null) {
        window.clearTimeout(refreshTimer);
      }

      refreshTimer = window.setTimeout(async () => {
        if (refreshing) {
          queued = true;
          return;
        }

        refreshing = true;
        try {
          await hydrateBilling();
        } finally {
          refreshing = false;
          if (queued) {
            queued = false;
            scheduleRefresh(350);
          }
        }
      }, delayMs);
    };

    const channel = supabase
      .channel(`billing-live-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "credits",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          scheduleRefresh(200);
        },
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "credit_transactions",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          scheduleRefresh(200);
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "subscriptions",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          scheduleRefresh(200);
        },
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          scheduleRefresh(0);
        }
      });

    return () => {
      if (refreshTimer !== null) {
        window.clearTimeout(refreshTimer);
      }
      void supabase.removeChannel(channel);
    };
  }, [hydrateBilling, user?.id]);

  const displayName = formatDisplayName(profile?.first_name, profile?.last_name);
  const pqrDefaultValues = {
    email: user?.email ?? "",
    name: displayName,
  };

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-ink/8 bg-white/96 backdrop-blur-xl">
        <div className="mx-auto flex h-16 w-full max-w-[1560px] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="flex min-w-0 items-center gap-3">
            <LanguageSwitcher />

            <NavLink
              to="/app"
              className="group flex min-w-0 items-center gap-3 text-ink no-underline transition"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center border-r border-ink/10 pr-3">
                <img
                  src="/images/logo.svg"
                  alt={t("common.appName")}
                  className="h-7 w-7 object-contain transition-transform duration-200 group-hover:scale-[1.03]"
                />
              </span>
              <span className="min-w-0">
                <span className="block truncate font-display text-lg font-semibold leading-none text-ink">
                  {t("common.appName")}
                </span>
                <span className="mt-1 block text-[10px] font-semibold uppercase tracking-[0.28em] text-ink/45">
                  {isProjectRoute ? t("projects.dashboard.title") : t("shell.nav.home")}
                </span>
              </span>
            </NavLink>
          </div>

          <div className="flex items-center gap-2">
            {creditBalance !== null ? (
              <div className="inline-flex items-center gap-1.5 rounded-full border border-white/75 bg-white/72 px-3 py-2 text-sm font-semibold text-ink shadow-sm">
                <Coins className="size-4 text-clay" />
                <span>{creditBalance}</span>
              </div>
            ) : null}

            <Button
              onClick={() => setIsPqrDialogOpen(true)}
              size="sm"
              type="button"
              variant="outline"
              className="rounded-full border-white/75 bg-white/72"
            >
              <LifeBuoy className="size-4" />
              {t("pqr.actions.contactUs")}
            </Button>

            {showAiMenu ? (
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full border border-white/75 bg-white/72 px-4 py-2.5 text-sm font-semibold text-ink shadow-sm transition hover:bg-white"
                  >
                    <FlaskConical className="size-4 text-clay" />
                    {activeAiOptionLabel}
                    <ChevronDown className="size-3 text-ink/60" />
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content
                    align="end"
                    sideOffset={10}
                    className="z-50 min-w-52 rounded-[1.5rem] border border-ink/10 bg-[#fffdf9] p-2 shadow-panel"
                  >
                    {aiOptions.map((option) => (
                      <DropdownMenu.Item asChild key={option.id}>
                        <Link
                          className="flex items-center justify-between gap-3 rounded-[1.1rem] px-4 py-2.5 text-sm text-ink outline-none transition hover:bg-ink/5 focus:bg-ink/5"
                          to={buildAiHref(option.id)}
                        >
                          <span>{option.label}</span>
                          {option.id === activeAiFeatureId ? (
                            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-clay">
                              {t("common.active", { defaultValue: "Active" })}
                            </span>
                          ) : null}
                        </Link>
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            ) : null}

            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  aria-label={t("common.openMenu")}
                  className="inline-flex items-center gap-2 rounded-full border border-white/75 bg-white/72 px-4 py-2.5 text-sm font-semibold text-ink shadow-sm transition hover:bg-white"
                >
                  <UserRound className="size-4 text-clay" />
                  <span className="max-w-32 truncate">{displayName || t("shell.account.label")}</span>
                  <ChevronDown className="size-3 text-ink/60" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={10}
                  className="z-50 min-w-56 rounded-[1.5rem] border border-ink/10 bg-[#fffdf9] p-2 shadow-panel"
                >
                  {tier ? (
                    <>
                      <div className="flex items-center gap-2 px-4 py-2.5">
                        <span className="rounded-full bg-clay/10 px-2.5 py-0.5 text-xs font-bold uppercase tracking-[0.18em] text-clay">
                          {t(`common.tiers.${tier}`)}
                        </span>
                        <span className="text-xs text-ink/45">{t("shell.account.label")}</span>
                      </div>
                      <DropdownMenu.Separator className="my-1.5 h-px bg-ink/10" />
                    </>
                  ) : null}

                  <DropdownMenu.Item asChild>
                    <Link
                      to="/app"
                      className="flex items-center gap-3 rounded-[1.1rem] px-4 py-2.5 text-sm text-ink outline-none transition hover:bg-ink/5 focus:bg-ink/5"
                    >
                      <Building2 className="size-4 text-clay" />
                      {t("shell.nav.home")}
                    </Link>
                  </DropdownMenu.Item>

                  <DropdownMenu.Item asChild>
                    <Link
                      to="/account/billing"
                      className="flex items-center gap-3 rounded-[1.1rem] px-4 py-2.5 text-sm text-ink outline-none transition hover:bg-ink/5 focus:bg-ink/5"
                    >
                      <CreditCard className="size-4 text-clay" />
                      {t("shell.account.billing")}
                    </Link>
                  </DropdownMenu.Item>

                  <DropdownMenu.Separator className="my-1.5 h-px bg-ink/10" />

                  <DropdownMenu.Item asChild>
                    <Link
                      to="/account/profile"
                      className="flex items-center gap-3 rounded-[1.1rem] px-4 py-2.5 text-sm text-ink outline-none transition hover:bg-ink/5 focus:bg-ink/5"
                    >
                      <UserRound className="size-4 text-clay" />
                      {t("shell.account.profile")}
                    </Link>
                  </DropdownMenu.Item>

                  <DropdownMenu.Item asChild>
                    <Link
                      to="/account/security"
                      className="flex items-center gap-3 rounded-[1.1rem] px-4 py-2.5 text-sm text-ink outline-none transition hover:bg-ink/5 focus:bg-ink/5"
                    >
                      <Shield className="size-4 text-clay" />
                      {t("shell.account.security")}
                    </Link>
                  </DropdownMenu.Item>

                  <DropdownMenu.Item asChild>
                    <Link
                      to="/account/requests"
                      className="flex items-center gap-3 rounded-[1.1rem] px-4 py-2.5 text-sm text-ink outline-none transition hover:bg-ink/5 focus:bg-ink/5"
                    >
                      <Inbox className="size-4 text-clay" />
                      {t("shell.account.requests")}
                    </Link>
                  </DropdownMenu.Item>

                  <DropdownMenu.Separator className="my-1.5 h-px bg-ink/10" />

                  <DropdownMenu.Item
                    onSelect={() => { void logOut(); }}
                    className="flex cursor-pointer items-center gap-3 rounded-[1.1rem] px-4 py-2.5 text-sm text-ink outline-none transition hover:bg-ink/5 focus:bg-ink/5"
                  >
                    <LogOut className="size-4 text-clay" />
                    {t("shell.account.logout")}
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </div>

        {isProjectRoute && projectId ? (
          <div className="border-t border-ink/8 bg-white">
            <div className="mx-auto flex w-full max-w-[1560px] flex-col gap-3 px-4 py-3 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8 xl:px-10">
              <ProjectDashboardNav
                activeSection={getActiveSection(pathname)}
                compact
                sections={sections}
              />

              <div className="flex flex-wrap items-center gap-2">
                <Button asChild size="sm" type="button" variant="outline" className="rounded-full border-white/75 bg-white/72">
                  <Link to={`/projects/${projectId}/stories/book-assembly`}>
                    {getBookAssemblyLabel(bookAssemblyLaunchState?.latestJobStatus)}
                  </Link>
                </Button>
                <Button
                  onClick={() => setSettingsOpen(true)}
                  size="sm"
                  type="button"
                  variant="outline"
                  className="rounded-full border-white/75 bg-white/72"
                >
                  <Settings className="size-4" />
                  Settings
                </Button>
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 rounded-full border border-white/75 bg-white/72 px-4 py-2.5 text-sm font-semibold text-ink shadow-sm transition hover:bg-white"
                    >
                      Project
                      <ChevronDown className="size-3 text-ink/60" />
                    </button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Portal>
                    <DropdownMenu.Content
                      align="end"
                      sideOffset={10}
                      className="z-50 min-w-52 rounded-[1.5rem] border border-ink/10 bg-[#fffdf9] p-2 shadow-panel"
                    >
                      {currentProjectSummary?.status === "active" ? (
                        <DropdownMenu.Item
                          className="flex cursor-pointer items-center gap-3 rounded-[1.1rem] px-4 py-2.5 text-sm text-ink outline-none transition hover:bg-ink/5 focus:bg-ink/5"
                          onSelect={() => {
                            if (currentProjectSummary) {
                              void archiveProject(currentProjectSummary.projectId);
                            }
                          }}
                        >
                          <Archive className="size-4 text-clay" />
                          {t("projects.actions.archive")}
                        </DropdownMenu.Item>
                      ) : null}
                      <DropdownMenu.Item
                        className="flex cursor-pointer items-center gap-3 rounded-[1.1rem] px-4 py-2.5 text-sm text-destructive outline-none transition hover:bg-ink/5 focus:bg-ink/5"
                        onSelect={() => {
                          if (currentProjectSummary) {
                            openDeleteDialog(currentProjectSummary);
                          }
                        }}
                      >
                        <Trash2 className="size-4" />
                        {t("projects.actions.delete")}
                      </DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              </div>
            </div>
          </div>
        ) : null}
      </header>

      <PQRFormDialog
        defaultValues={pqrDefaultValues}
        isAuthenticated
        onOpenChange={setIsPqrDialogOpen}
        open={isPqrDialogOpen}
      />
      <ProjectSettingsDialog
        onOpenChange={setSettingsOpen}
        open={settingsOpen}
        project={currentProjectSummary}
      />
    </>
  );
}
